'use client';

import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { useCallback, useMemo } from 'react';

import { buildProductsRequest } from '@eliadmin/core/src/api/products/product-list-request';
import {
  fetchProductsWithSignal,
  productsInfiniteQueryKey,
  retryProductsQuery,
} from '@eliadmin/core/src/api/products/products-query';
import { normalizeCategoryPage } from '@eliadmin/core/src/components/categories/category-query-state';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import {
  buildProductsQuery,
  parseProductSearchParams,
} from '@eliadmin/core/src/utils/product-utils';

/**
 * The products of the search page – the request of the core search page (`search`, the URL
 * parameters `parseProductSearchParams` reads, the store page size) with the sort and the page of
 * the scoped category store (`SearchView` – URL `order`, `orderBy`, `page`), loaded like the
 * category listing: the core `productsInfiniteQueryKey`, „show more“ appends the next page, the
 * previous result stays on screen (`isUpdating`) while a new sort or page loads. No sort in the URL
 * sends none, so the API answers in its search relevance order (the core page sent `position`).
 */
export function useSearchProducts(search: string) {
  const locale = useLocaleStore(state => state.currentLocale);
  const authToken = useCustomerStore(state => state.me?.authToken);
  const isInitialized = useCustomerStore(state => state.isInitialized);
  const perPage =
    useGlobalStore(state => state.generalLayout?.pagination?.paginationProducts) || 12;
  const sort = useCategoryStore(state => state.filter.sort);
  const paramsReady = useCategoryStore(state => state.paramsReady);
  const storedPage = useCategoryStore(state => state.page);
  const searchParams = useSearchParams();

  const currentPage = normalizeCategoryPage(storedPage ?? searchParams.get('page'));
  // Same gate as the category listing: the customer (prices, token) and the URL state first.
  const queryReady = isInitialized !== false && paramsReady !== false;

  const queryWithoutPage = useMemo(
    () =>
      buildProductsQuery({
        ...parseProductSearchParams(searchParams),
        search,
        results: perPage,
        order: sort.order,
        orderBy: sort.orderBy,
        page: undefined,
      }),
    [searchParams, search, perPage, sort],
  );
  const requestUri = buildProductsRequest({ ...queryWithoutPage, page: currentPage }).uri;

  const query = useInfiniteQuery({
    queryKey: productsInfiniteQueryKey(locale, requestUri, authToken, currentPage),
    placeholderData: keepPreviousData,
    retry: retryProductsQuery,
    queryFn: ({ pageParam, signal }) =>
      fetchProductsWithSignal({
        query: { ...queryWithoutPage, page: pageParam },
        locale,
        authToken,
        signal,
      }),
    getNextPageParam: last =>
      last.meta.current_page < last.meta.last_page ? last.meta.current_page + 1 : undefined,
    initialPageParam: currentPage,
    enabled: queryReady,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  const loadMore = useCallback(() => {
    if (queryReady && hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [queryReady, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const pages = query.data?.pages ?? [];
  const first = pages[0]?.meta;
  const last = pages[pages.length - 1]?.meta;

  return {
    query,
    /** One entry per loaded page – the page-wise `view_item_list` events. */
    pages,
    products: pages.flatMap(page => page.data),
    perPage,
    /** Loaded page range and totals of the current result (undefined before the first response). */
    meta: first && last ? { first, last } : undefined,
    /** A new sort / page is loading over the previous result. */
    isUpdating: query.isPlaceholderData,
    loadMore,
  };
}
